import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  FileSpreadsheet,
  Loader2,
  Upload,
} from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canUpload } from '@/lib/roles'
import { projectsApi, ImportBatchDetail, ProjectDetail } from '@/lib/projectsApi'
import {
  parseWorkbook,
  remapRows,
  isParseError,
  ParseResult,
  StagedRow,
} from '@/lib/workbookParser'
import { takePendingImport } from '@/lib/pendingImport'
import { API_BASE_URL, fetchWithAuthRetry } from '@/lib/apiClient'

type Step = 'upload' | 'staging' | 'preview'

// field → label for the column-mapping editor. keys must match ParsedField in workbookParser
const MAPPING_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'function_name',        label: 'Functions (category)' },
  { key: 'system_description',   label: 'System (description)' },
  { key: 'system_name',          label: 'System Name (primary)' },
  { key: 'vendor_name_raw',      label: 'Vendor' },
  { key: 'contact_full_name',    label: 'Contact Person' },
  { key: 'contact_email',        label: 'Email' },
  { key: 'contact_phone',        label: 'Phone' },
  { key: 'type_approval_status', label: 'TA Status' },
  // the shipyard's own owner for the request. Listed here so it is
  // visible in the mapping editor and the preview — parsed_data is
  // persisted either way, and a field nobody can see is a field nobody
  // knows they have.
  { key: 'shipyard_contact_raw',  label: 'Yard PIC (shipyard contact)' },
]

export const ImportWorkbookPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams]    = useSearchParams()
  const navigate          = useNavigate()
  const { success, error: showError } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep]             = useState<Step>('upload')
  const [dragOver, setDragOver]     = useState(false)
  const [file, setFile]             = useState<File | null>(null)
  const [parsing, setParsing]       = useState(false)
  const [stagedRows, setStagedRows] = useState<StagedRow[]>([])
  const [sheetUsed, setSheetUsed]   = useState('')
  const [headerRow, setHeaderRow]   = useState(0)
  const [batchId, setBatchId]       = useState<string | null>(null)
  const [stagingError, setStagingError] = useState<string | null>(null)

  const [, setPreviewBatch]   = useState<ImportBatchDetail | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)

  // column-mapping editor state: parseResult is the raw parser output, customColMap is the user's override
  const [parseResult, setParseResult]   = useState<ParseResult | null>(null)
  const [customColMap, setCustomColMap] = useState<Record<string, number> | null>(null)
  const [applyingMap, setApplyingMap]   = useState(false)

  const [committing, setCommitting] = useState(false)
  const [project, setProject]       = useState<ProjectDetail | null>(null)

  const { user } = useAuth()
  const canImport = canUpload(user)
  useEffect(() => {
    if (!canImport) {
      showError('Workbook import requires admin role')
      navigate('/dashboard')
    }
  }, [canImport, navigate, showError])

  useEffect(() => {
    if (projectId) {
      projectsApi.getProject(projectId).then(setProject).catch(() => {})
    }
  }, [projectId])

  useEffect(() => {
    const existingId = searchParams.get('batch')
    if (existingId && step === 'upload') {
      setLoadingPreview(true)
      projectsApi.getBatch(existingId)
        .then(batch => { setPreviewBatch(batch); setBatchId(existingId); setStep('preview') })
        .catch(err => showError(err.message ?? 'Failed to load batch'))
        .finally(() => setLoadingPreview(false))
    }
  }, [])

  useEffect(() => {
    const pending = takePendingImport()
    if (pending) processFile(pending)
  }, [])

  const processFile = useCallback(async (f: File) => {
    setFile(f)
    setParsing(true)
    setStagedRows([])
    setStagingError(null)

    try {
      const buffer   = await f.arrayBuffer()
      const XLSX     = await import('xlsx')
      const workbook = XLSX.read(buffer, { type: 'array' })
      const result   = parseWorkbook(workbook)

      if (isParseError(result)) {
        showError('Parse error', result.error)
        // telemetry (2026-06-12): ship the rejected workbook's sheet names
        // + headers to the server audit log so the format inventory stays
        // queryable without the user pasting error text. fire-and-forget,
        // a telemetry failure must never affect the user flow
        if (result.sheets && result.sheets.length > 0) {
          fetchWithAuthRetry(`${API_BASE_URL}/api/imports/parse-rejected`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_id: projectId, filename: f.name, sheets: result.sheets }),
          }).catch(() => {/* telemetry only */})
        }
        return
      }

      const { rows, skipped_count, sheet_used, header_row } = result
      const importableRows = rows.filter(r => !r.skipped)

      if (importableRows.length === 0) {
        showError('No data rows', `All ${skipped_count} rows were blank or decorative.`)
        return
      }

      setStagedRows(rows)
      setSheetUsed(sheet_used)
      setHeaderRow(header_row)
      setParseResult(result)
      setCustomColMap(null)

      // auto-stage to backend
      setStep('staging')
      try {
        const stageResult = await projectsApi.stageBatch({
          project_id:        projectId!,
          original_filename: f.name,
          sheet_name:        sheet_used,
          import_type:       result.format,
          rows:              importableRows.map(r => ({
            row_no:            r.row_no,
            raw_data:          r.raw_data,
            parsed_data:       r.parsed_data,
            validation_errors: r.validation_errors.length ? r.validation_errors : null,
            is_valid:          r.is_valid,
          })),
        })
        setBatchId(stageResult.batch_id)
        setStep('preview')
      } catch (err: any) {
        setStagingError(err.message ?? 'Failed to stage import')
        setStep('preview') // still show local preview, commit disabled
      }
    } catch (err: any) {
      showError('Failed to parse workbook', err.message ?? 'Unknown error')
    } finally {
      setParsing(false)
    }
  }, [projectId, showError])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) processFile(f)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.items?.[0]?.kind === 'file') setDragOver(true)
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (!f) return
    if (!f.name.match(/\.xlsx?$/i)) {
      showError('Wrong file type', 'Please drop an .xlsx file.')
      return
    }
    processFile(f)
  }

  const applyMapping = async () => {
    if (!parseResult) return
    const map = customColMap ?? parseResult.colMap
    const fillDownCol = map['function_name']
    const newRows = remapRows(
      parseResult.rawMatrix,
      parseResult.headerIdx,
      map,
      fillDownCol !== undefined ? [fillDownCol] : []
    )
    setStagedRows(newRows)

    // re-stage to server so commit reflects the new mapping (existing draft batch is abandoned)
    const importableRows = newRows.filter(r => !r.skipped)
    if (importableRows.length === 0) {
      showError('No data rows', 'The new mapping produced no importable rows.')
      return
    }
    setApplyingMap(true)
    setStagingError(null)
    try {
      const staged = await projectsApi.stageBatch({
        project_id:        projectId!,
        original_filename: file?.name ?? 'unknown.xlsx',
        sheet_name:        sheetUsed,
        import_type:       parseResult?.format,
        rows:              importableRows.map(r => ({
          row_no:            r.row_no,
          raw_data:          r.raw_data,
          parsed_data:       r.parsed_data,
          validation_errors: r.validation_errors.length ? r.validation_errors : null,
          is_valid:          r.is_valid,
        })),
      })
      setBatchId(staged.batch_id)
      success('Mapping applied', 'Preview and commit target updated')
    } catch (err: any) {
      setStagingError(err.message ?? 'Failed to re-stage after mapping change')
    } finally {
      setApplyingMap(false)
    }
  }

  const existingSystemCount = project?.system_count ?? 0

  const handleCommit = async () => {
    if (!batchId) return
    setCommitting(true)
    try {
      // re-import UPSERTS: matched systems update in place (assessments /
      // inventory / contacts stay attached), new ones insert, absent ones
      // are left alone. the old auto replace:true wipe is gone — it
      // orphaned inventory + Word-RA mappings via ON DELETE SET NULL.
      const result = await projectsApi.commitBatch(batchId)
      // no interstitial, land straight back on the project with the systems
      // imported. the toast carries the counts
      const parts = [
        `${result.systems_created} new`,
        `${result.systems_updated} updated`,
        result.junk_skipped > 0 ? `${result.junk_skipped} junk row${result.junk_skipped === 1 ? '' : 's'} skipped` : null,
      ].filter(Boolean)
      success(`Import complete — ${parts.join(', ')}`)
      navigate(`/projects/${projectId}`)
    } catch (err: any) {
      showError(err.message ?? 'Failed to commit import')
    } finally {
      setCommitting(false)
    }
  }

  const displayRows  = stagedRows.filter(r => !r.skipped)
  const cleanCount   = displayRows.filter(r => r.classification === 'clean').length
  const warnCount    = displayRows.filter(r => r.classification === 'warning').length
  const invalidCount = displayRows.filter(r => r.classification === 'invalid').length
  const validCount   = cleanCount + warnCount   // committed on commit
  const skippedCount = stagedRows.filter(r => r.skipped).length

  return (
    <div
      className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
      onDragOver={step === 'upload' ? handleDragOver : undefined}
      onDragLeave={step === 'upload' ? handleDragLeave : undefined}
      onDrop={step === 'upload' ? handleDrop : undefined}
    >
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (step === 'preview') { setStep('upload'); return }
              navigate(`/projects/${projectId}`)
            }}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <Breadcrumb items={[
              { label: 'Dashboard', to: '/dashboard' },
              ...(project ? [
                { label: project.shipyard_name ?? 'Unassigned' },
                { label: project.hull_number, to: `/projects/${projectId}` },
              ] : []),
              { label: 'Import Workbook' },
            ]} />
            <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2 mt-0.5">
              <FileSpreadsheet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Import System Register
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {step === 'upload'  && 'Drop or select the yard workbook (.xlsx). Sheet "2. System Information" is parsed automatically.'}
              {step === 'staging' && 'Staging rows…'}
              {step === 'preview' && `${file?.name ?? 'Draft'} — sheet: ${sheetUsed} (header row ${headerRow})`}
            </p>
          </div>
        </div>
      </div>

      {/* Step bar */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="px-4 sm:px-6 py-3 flex gap-6 text-sm">
          {(['upload', 'preview'] as const).map((s, i) => (
            <span key={s} className={`flex items-center gap-1.5 ${
              (step === 'staging' && s === 'preview') || step === s
                ? 'text-blue-600 dark:text-blue-400 font-medium'
                : 'text-slate-400'
            }`}>
              <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${
                (step === 'staging' && s === 'preview') || step === s
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
              }`}>{i + 1}</span>
              {s === 'upload' ? 'Upload' : 'Review & Commit'}
            </span>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 max-w-6xl mx-auto space-y-6">

        {/* Upload */}
        {(step === 'upload' || step === 'staging') && (
          <>
            {loadingPreview ? (
              <div className="py-16 text-center text-slate-500 dark:text-slate-400">
                <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin" />
                Loading draft…
              </div>
            ) : (
              <Card className={`bg-white dark:bg-slate-800 border-2 transition-colors ${
                dragOver
                  ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-dashed border-slate-300 dark:border-slate-600'
              }`}>
                <CardContent className="py-12">
                  <div
                    className="text-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {parsing || step === 'staging' ? (
                      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
                        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                        <p className="font-medium">
                          {parsing ? 'Parsing sheet…' : 'Staging rows to server…'}
                        </p>
                        {file && (
                          <p className="text-sm">{file.name}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
                        <Upload className={`h-14 w-14 transition-colors ${
                          dragOver ? 'text-blue-500' : 'text-slate-300 dark:text-slate-600'
                        }`} />
                        <div>
                          <p className="font-semibold text-slate-700 dark:text-slate-200">
                            {dragOver ? 'Drop to import' : 'Drop workbook here, or click to browse'}
                          </p>
                          <p className="text-sm mt-1">.xlsx — sheet "2. System Information" is auto-detected</p>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Preview */}
        {step === 'preview' && (
          <>
            {/* Summary bar */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                {cleanCount} clean row{cleanCount !== 1 ? 's' : ''}
              </span>
              {warnCount > 0 && (
                <span className="flex items-center gap-1.5 text-amber-500">
                  <AlertTriangle className="h-4 w-4" />
                  {warnCount} with warnings (still committed)
                </span>
              )}
              {invalidCount > 0 && (
                <span className="flex items-center gap-1.5 text-red-500 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  {invalidCount} invalid (skipped on commit)
                </span>
              )}
              {skippedCount > 0 && (
                <span className="text-slate-400">
                  {skippedCount} blank row{skippedCount !== 1 ? 's' : ''} ignored
                </span>
              )}
            </div>

            {existingSystemCount > 0 && (
              <div className="flex items-center gap-3 px-3 py-2 mb-3 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-amber-800 dark:text-amber-200">
                    This project already has {existingSystemCount} system{existingSystemCount !== 1 ? 's' : ''}
                  </div>
                  <div className="text-[10px] text-amber-600 dark:text-amber-400">
                    Matching systems are updated in place (blank fields filled; assessments, inventory
                    and contacts stay attached). New systems are added; existing ones are never deleted.
                  </div>
                </div>
              </div>
            )}

            {stagingError && (
              <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                Staging failed: {stagingError} — commit is disabled until staging succeeds.
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-3"
                  onClick={async () => {
                    setStagingError(null)
                    try {
                      const importableRows = displayRows
                      const r = await projectsApi.stageBatch({
                        project_id: projectId!,
                        original_filename: file?.name ?? 'unknown.xlsx',
                        sheet_name: sheetUsed,
                        rows: importableRows.map(r => ({
                          row_no: r.row_no, raw_data: r.raw_data,
                          parsed_data: r.parsed_data,
                          validation_errors: r.validation_errors.length ? r.validation_errors : null,
                          is_valid: r.is_valid,
                        })),
                      })
                      setBatchId(r.batch_id)
                    } catch (e: any) {
                      setStagingError(e.message ?? 'Still failing')
                    }
                  }}
                >
                  Retry staging
                </Button>
              </div>
            )}

            {parseResult && parseResult.format === 'system_register' && (
              <>
                <details className="mb-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
                  <summary className="px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg">
                    Adjust column mapping
                  </summary>
                  <div className="px-3 pb-3">
                    <p className="text-[10px] text-slate-400 mb-3">
                      Change which workbook column feeds each field. The parser auto-detected a mapping from the header row — adjust below if it got it wrong, then click Apply.
                    </p>

                    <div className="overflow-x-auto mb-3 border border-slate-100 dark:border-slate-700/50 rounded">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-900/40">
                            {parseResult.headers.map((h, i) => (
                              <th key={i} className="px-2 py-1 text-left text-slate-400 font-normal whitespace-nowrap">
                                {h || `Col ${i + 1}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {parseResult.rawMatrix
                            .slice(parseResult.headerIdx + 1, parseResult.headerIdx + 4)
                            .map((row, ri) => (
                              <tr key={ri} className="border-t border-slate-100 dark:border-slate-700/50">
                                {parseResult.headers.map((_, ci) => (
                                  <td key={ci} className="px-2 py-0.5 text-slate-600 dark:text-slate-300 max-w-[120px] truncate">
                                    {String((row as unknown[])?.[ci] ?? '').slice(0, 30) || '—'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {MAPPING_FIELDS.map(({ key, label }) => {
                        const activeMap = customColMap ?? parseResult.colMap
                        const val = activeMap[key]
                        return (
                          <div key={key}>
                            <label className="text-[10px] text-slate-500 block mb-0.5">{label}</label>
                            <select
                              value={val ?? ''}
                              onChange={e => {
                                const base = customColMap ?? parseResult.colMap
                                const newMap: Record<string, number> = { ...base }
                                if (e.target.value === '') {
                                  delete newMap[key]
                                } else {
                                  newMap[key] = parseInt(e.target.value)
                                }
                                setCustomColMap(newMap)
                              }}
                              className="w-full h-7 px-1.5 text-[11px] border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            >
                              <option value="">— not mapped —</option>
                              {parseResult.headers.map((h, i) => (
                                <option key={i} value={i}>{h || `Column ${i + 1}`}</option>
                              ))}
                            </select>
                          </div>
                        )
                      })}
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <button
                        onClick={() => setCustomColMap(null)}
                        className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      >
                        Reset to auto-detected
                      </button>
                      <Button
                        size="sm"
                        onClick={applyMapping}
                        disabled={applyingMap}
                      >
                        {applyingMap ? (
                          <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Applying…</>
                        ) : (
                          'Apply mapping'
                        )}
                      </Button>
                    </div>
                  </div>
                </details>

                <div className="text-[10px] text-slate-400 mb-2">
                  {customColMap ? (
                    <span className="text-blue-500 dark:text-blue-400">Custom column mapping applied</span>
                  ) : (
                    <span>Auto-detected column mapping</span>
                  )}
                </div>
              </>
            )}

            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-base">
                      {displayRows.length} row{displayRows.length !== 1 ? 's' : ''} parsed
                    </CardTitle>
                    <CardDescription>
                      {validCount} will be committed ({cleanCount} clean, {warnCount} with warnings)
                      {skippedCount > 0 ? ` · ${skippedCount} blank rows ignored` : ''}
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleCommit}
                    disabled={committing || !batchId || validCount === 0}
                  >
                    {committing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Committing…</>
                    ) : !batchId ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Staging…</>
                    ) : (
                      <><CheckCircle className="h-4 w-4 mr-2" />Commit Import</>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 dark:bg-slate-700 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium w-10">#</th>
                        <th className="text-left px-3 py-2 font-medium">Functions</th>
                        <th className="text-left px-3 py-2 font-medium">System</th>
                        <th className="text-left px-3 py-2 font-medium">Vendor</th>
                        <th className="text-left px-3 py-2 font-medium">System Name</th>
                        <th className="text-left px-3 py-2 font-medium">Contact(s)</th>
                        <th className="text-left px-3 py-2 font-medium">Email(s)</th>
                        <th className="text-left px-3 py-2 font-medium">Phone(s)</th>
                        <th className="text-left px-3 py-2 font-medium">TA Status</th>
                        <th className="text-left px-3 py-2 font-medium w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map(row => {
                        const pd = row.parsed_data
                        const isMulti = pd.multi_contact && pd.contacts && pd.contacts.length > 1
                        const rowBg = row.classification === 'invalid'
                          ? 'bg-red-50 dark:bg-red-950/20'
                          : row.classification === 'warning'
                          ? 'bg-amber-50 dark:bg-amber-950/10'
                          : ''
                        return (
                          <tr key={row.row_no} className={`border-b border-slate-200 dark:border-slate-700 ${rowBg}`}>
                            <td className="px-3 py-2 text-slate-400">{row.row_no}</td>
                            <td className="px-3 py-2 max-w-[100px] truncate">{pd.function_name ?? '—'}</td>
                            <td className="px-3 py-2 max-w-[150px] truncate">{pd.system_description ?? '—'}</td>
                            <td className="px-3 py-2 max-w-[110px] truncate">{pd.vendor_name_raw ?? '—'}</td>
                            <td className="px-3 py-2 max-w-[110px] truncate">{pd.system_name ?? '—'}</td>
                            <td className="px-3 py-2 max-w-[130px]">
                              {isMulti ? (
                                <span className="text-blue-600 dark:text-blue-400">
                                  {pd.contacts!.map(c => c.full_name ?? '—').join(' / ')}
                                </span>
                              ) : (pd.contact_full_name ?? '—')}
                            </td>
                            <td className="px-3 py-2 max-w-[160px] truncate">
                              {(() => {
                                if (isMulti) {
                                  // dedup: Vendor Tracking applies one shared email
                                  // to every name in a multi-contact cell. lowercase
                                  // matches what citext stores at the DB layer
                                  const emails = Array.from(new Set(
                                    pd.contacts!
                                      .map(c => c.email?.trim().toLowerCase())
                                      .filter((e): e is string => !!e),
                                  ))
                                  if (emails.length === 0) return '—'
                                  return (
                                    <span className="text-blue-600 dark:text-blue-400">
                                      {emails.join(' / ')}
                                    </span>
                                  )
                                }
                                return pd.contact_email ?? '—'
                              })()}
                            </td>
                            <td className="px-3 py-2 max-w-[140px] truncate">
                              {(() => {
                                if (isMulti) {
                                  // dedup: Vendor Tracking applies one shared phone
                                  // to every name in a multi-contact cell
                                  const phones = Array.from(new Set(
                                    pd.contacts!
                                      .map(c => c.phone?.trim())
                                      .filter((p): p is string => !!p),
                                  ))
                                  if (phones.length === 0) return '—'
                                  return (
                                    <span className="text-blue-600 dark:text-blue-400">
                                      {phones.join(' / ')}
                                    </span>
                                  )
                                }
                                const phone = (pd.contact_phone ?? '').trim()
                                return phone || '—'
                              })()}
                            </td>
                            <td className="px-3 py-2 max-w-[80px] truncate">{pd.type_approval_status ?? '—'}</td>
                            <td className="px-3 py-2">
                              {row.classification === 'invalid' ? (
                                <span title={row.validation_errors.join('; ')} className="cursor-help">
                                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                                </span>
                              ) : row.classification === 'warning' ? (
                                <span title={row.validation_errors.join('; ')} className="cursor-help">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                </span>
                              ) : (
                                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

      </div>
    </div>
  )
}
